import { Messages } from "@lingui/core"

// Applied when a primitive placeholder opens a message, so a lowercase literal
// still reads as a sentence start. Only the first character is touched — the
// rest is admin-authored and must keep its casing, otherwise acronyms and
// multi-word terms get flattened ("AFT Participants" -> "Aft participants").
const capitalizeFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Community literal overrides arrive as raw strings (e.g. "Phase {roundNumber}").
// Lingui's pre-compiled catalogs encode placeholders as token arrays
// (["Phase ", ["roundNumber"]]) and only interpolate that array form — a raw
// string is returned verbatim in production builds, so "{roundNumber}" / "{100}"
// would render literally. Recompile the override into the same token shape Lingui
// expects so both runtime variables and {100}-style primitive terms resolve.
const compileOverride = (value: string): Messages[string] => {
  if (!value.includes("{")) return value

  const tokens: Array<string | [string]> = []
  const placeholder = /\{([^{}]+)\}/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = placeholder.exec(value)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(value.slice(lastIndex, match.index))
    }
    tokens.push([match[1]])
    lastIndex = placeholder.lastIndex
  }

  if (lastIndex < value.length) {
    tokens.push(value.slice(lastIndex))
  }

  return tokens
}

// Compile community literal overrides into Lingui's token shape. Applied only to
// the overrides (not the base catalog), since base messages are already compiled
export const compileLiteralOverrides = (
  literals: { [key: string]: string | undefined } | undefined,
): Messages => {
  if (!literals) return {}

  const compiled: Messages = {}
  for (const [key, value] of Object.entries(literals)) {
    if (typeof value === "string") {
      compiled[key] = compileOverride(value)
    }
  }

  return compiled
}

export const formatLanguages = (
  initial: Messages,
  replacements: { [key: string]: string },
): Messages => {
  return Object.keys(initial).reduce((formatted, key) => {
    let value = initial[key]

    if (Array.isArray(value)) {
      let hasReplacement = false
      let hasUnreplacedPlaceholder = false
      value = value.map((message, index) => {
        if (Array.isArray(message)) {
          const replacementMessage = replacements[message[0]]
          if (replacementMessage) {
            hasReplacement = true
            return index === 0
              ? capitalizeFirst(replacementMessage)
              : replacementMessage
          }
          hasUnreplacedPlaceholder = true
          return message
        }

        return message
      })
      // Only flatten to a string when all placeholders were replaced. Otherwise
      // join() would coerce remaining ["userName"]-style placeholders into the
      // literal string "userName", breaking Lingui's runtime interpolation.
      if (hasReplacement && !hasUnreplacedPlaceholder) {
        value = value.join("")
      }
    }

    formatted[key] = value

    return formatted
  }, {} as Messages)
}
